import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using MultiSellerPro™ - including our website (MultiSellerPro™.com), web platform, and Chrome browser extension - you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do not use our services.

These Terms apply to all users, including visitors, registered users, and subscribers. We reserve the right to update these Terms at any time. Continued use of MultiSellerPro™ after changes are posted constitutes acceptance of the revised Terms.

Last updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
  },
  {
    id: 'description',
    title: '2. Description of Services',
    content: `MultiSellerPro™ provides a suite of tools designed for online sellers, resellers, and arbitrage professionals. Our services include:

**Web Platform Features:**
• Live Scan - real-time product sourcing and analysis
• Reverse Scan - reverse lookup by product name or UPC
• Storefront Scan - analyze seller storefronts across marketplaces
• Wholesale Scan - bulk product analysis for wholesale sourcing
• A2A Deals - Amazon-to-Amazon deal identification
• Local Scan - local marketplace sourcing tools
• Favorites - save and track products across sessions
• Dashboard - account management and analytics

**Chrome Extension Features:**
• On-page product overlays across Amazon, Walmart, and Ebay
• Real-time profit calculations and fee analysis
• Right-click search from any webpage
• Amazon Influencer Program (AIP) tools
• Cross-tab search state memory

Feature availability depends on your subscription plan (Beginner or Pro). We reserve the right to modify, suspend, or discontinue any feature at any time with reasonable notice.`
  },
  {
    id: 'accounts',
    title: '3. Accounts & Registration',
    content: `**Account Creation**
To access most features, you must create an account using a valid email address and password. You agree to provide accurate, current, and complete information during registration and to keep your account information updated.

**Account Security**
You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately at contact@multisellerpro.com if you suspect unauthorized access to your account. MultiSellerPro™ is not liable for losses resulting from unauthorized use of your account due to your failure to secure your credentials.

**One Account Per User**
Each user may maintain only one account. Creating duplicate accounts to circumvent plan restrictions or access limits is prohibited and may result in suspension of all associated accounts.

**Age Requirement**
You must be at least 13 years of age to use MultiSellerPro™. By registering, you represent that you meet this requirement.`
  },
  {
    id: 'subscriptions',
    title: '4. Subscriptions & Billing',
    content: `**Subscription Plans**
MultiSellerPro™ offers tiered subscription plans (Beginner, Pro) with different feature sets. Plan details, pricing, and included features are described on our Pricing page and are subject to change with notice.

**Billing**
Subscriptions are billed on a recurring basis (monthly or annually depending on your selected plan). By subscribing, you authorize us to charge your payment method on a recurring basis until you cancel.

**Cancellation**
You may cancel your subscription at any time through your account settings or by contacting contact@multisellerpro.com. Cancellation takes effect at the end of the current billing period. You will retain access to paid features until the period ends.

**Refunds**
All subscription fees are non-refundable except where required by applicable law. If you believe you were charged in error, contact us within 14 days of the charge at contact@multisellerpro.com.

**Plan Downgrades & Upgrades**
You may upgrade your plan at any time; the change takes effect immediately and is prorated. Downgrades take effect at the next billing cycle.

**Failed Payments**
If a payment fails, we will attempt to retry the charge. If payment cannot be collected, your account may be downgraded or suspended until payment is resolved.`
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable Use Policy',
    content: `You agree to use MultiSellerPro™ only for lawful purposes and in accordance with these Terms. You must not:

• Use the service for any illegal purpose or in violation of any local, national, or international law
• Scrape, copy, or reproduce MultiSellerPro™'s platform, UI, or proprietary data using automated tools beyond normal browser use
• Attempt to reverse engineer, decompile, or extract the source code of the extension or platform
• Share, resell, or sublicense your account or subscription to third parties
• Use the service to transmit spam, malware, or any harmful code
• Interfere with or disrupt the integrity or performance of our services or servers
• Create multiple accounts to gain unauthorized access to features or bypass plan restrictions
• Use the extension to collect data from marketplaces in violation of those platforms' terms of service
• Impersonate any person or entity, or misrepresent your affiliation with any person or entity

Violation of this policy may result in immediate suspension or termination of your account without refund.`
  },
  {
    id: 'ip',
    title: '6. Intellectual Property',
    content: `**Our Content**
All content, features, and functionality of MultiSellerPro™ - including but not limited to the software, UI design, logos, text, graphics, and underlying code - are owned by MultiSellerPro™ and are protected by copyright, trademark, and other intellectual property laws.

**Limited License**
We grant you a limited, non-exclusive, non-transferable, revocable license to use MultiSellerPro™ solely for your personal or internal business use in accordance with these Terms. This license does not include the right to copy, modify, distribute, sell, or sublicense any part of our service.

**Your Content**
You retain ownership of any content you input into MultiSellerPro™ (e.g., product lists, notes). By submitting content, you grant us a limited license to use it to provide and improve our services.

**Feedback**
If you submit suggestions or feedback about our services, you grant us the right to use that feedback without obligation or compensation to you.`
  },
  {
    id: 'third-party',
    title: '7. Third-Party Platforms & Data',
    content: `**Marketplace Data**
MultiSellerPro™ displays publicly available data from third-party marketplaces (Amazon, Walmart, eBay). We are not affiliated with, endorsed by, or sponsored by any of these platforms.

**Accuracy Disclaimer**
Pricing, fee, and product data displayed by MultiSellerPro™ is sourced from public marketplace listings and may not always be current or accurate. All profit calculations are estimates based on publicly available information and standard fee structures which may change. You are solely responsible for verifying data before making sourcing or purchasing decisions.

**Third-Party Terms**
Your use of supported marketplaces is governed by those platforms' own terms of service. MultiSellerPro™ does not assume responsibility for any violations of third-party platform terms that may result from your use of our extension.

**External Links**
Our platform may contain links to third-party websites. We are not responsible for the content or privacy practices of those sites.`
  },
  {
    id: 'disclaimers',
    title: '8. Disclaimers & Limitation of Liability',
    content: `**No Financial Advice**
MultiSellerPro™ is a data and analysis tool. Nothing on our platform constitutes financial, investment, or business advice. Profit calculations are estimates only. You assume all risk for any buying, selling, or sourcing decisions made using our service.

**Service Availability**
We strive for high availability but do not guarantee that the service will be uninterrupted, error-free, or available at all times. We may perform maintenance or updates that temporarily limit access.

**Disclaimer of Warranties**
MultiSellerPro™ is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.

**Limitation of Liability**
To the fullest extent permitted by law, MultiSellerPro™ and its owners, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of or inability to use the service, even if we have been advised of the possibility of such damages.

Our total liability to you for any claim arising from these Terms or your use of the service shall not exceed the amount you paid us in the 3 months preceding the claim.`
  },
  {
    id: 'termination',
    title: '9. Termination',
    content: `**By You**
You may terminate your account at any time by canceling your subscription and contacting us at contact@multisellerpro.com to request account deletion. Upon termination, your right to use the service ceases immediately.

**By Us**
We reserve the right to suspend or terminate your account at our sole discretion, with or without notice, if we determine that you have violated these Terms, engaged in fraudulent activity, or for any other reason we deem appropriate. In cases of serious violations (e.g., abuse, fraud), no refund will be issued.

**Effect of Termination**
Upon termination, all licenses granted to you under these Terms will immediately cease. Sections of these Terms that by their nature should survive termination (including intellectual property, disclaimers, and limitation of liability) shall survive.`
  },
  {
    id: 'governing-law',
    title: '10. Governing Law & Disputes',
    content: `These Terms shall be governed by and construed in accordance with applicable law. Any dispute arising from these Terms or your use of MultiSellerPro™ shall first be attempted to be resolved informally by contacting us at contact@multisellerpro.com.

If informal resolution fails, disputes shall be resolved through binding arbitration rather than in court, except that either party may seek injunctive relief in court for intellectual property violations.

You waive any right to participate in a class action lawsuit or class-wide arbitration against MultiSellerPro™.`
  },
  {
    id: 'affiliate',
    title: '11. Affiliate Program',
    content: `MultiSellerPro™ may operate an affiliate or referral program. Participants must comply with applicable laws and our affiliate guidelines. We reserve the right to modify, suspend, or terminate the affiliate program at any time.

Affiliate commissions are tracked via referral codes stored in your browser. Fraudulent referrals or self-referrals are prohibited and will result in forfeiture of commissions and account termination.`
  },
  {
    id: 'changes',
    title: '12. Changes to These Terms',
    content: `We may revise these Terms at any time. When we make material changes, we will update the date at the top of this page and notify you via email or an in-app notice where required. Your continued use of MultiSellerPro™ after changes take effect constitutes your acceptance of the revised Terms.

If you do not agree with updated Terms, you must stop using the service and cancel your subscription.`
  },
  {
    id: 'contact',
    title: '13. Contact',
    content: `For questions about these Terms, contact us:

📧 contact@multisellerpro.com

For billing or account support:
📧 contact@multisellerpro.com

We aim to respond within 5 business days.`
  }
];

const AccordionSection = ({ section, isOpen, onToggle }) => {
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden mb-3 shadow-sm hover:shadow-md transition-shadow duration-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-gray-50 transition-colors duration-150 text-left"
      >
        <span className="font-semibold text-gray-800 text-base">{section.title}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-blue-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 py-5 bg-gray-50 border-t border-gray-100">
          <div className="text-gray-600 text-sm leading-relaxed">
            {section.content.split('\n').map((line, i) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="font-semibold text-gray-800 mt-4 mb-1 first:mt-0">{line.replace(/\*\*/g, '')}</p>;
              }
              if (line.startsWith('• ')) {
                return (
                  <p key={i} className="flex items-start gap-2 ml-2 my-1">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">•</span>
                    <span>{line.slice(2)}</span>
                  </p>
                );
              }
              if (line.startsWith('📧')) {
                return <p key={i} className="font-medium text-blue-600 mt-2">{line}</p>;
              }
              return line ? <p key={i} className="my-1">{line}</p> : <div key={i} className="h-2" />;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TermsPage = () => {
  const [openSections, setOpenSections] = useState(new Set(['acceptance']));

  const toggle = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSections(new Set(sections.map(s => s.id)));
  const collapseAll = () => setOpenSections(new Set());

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6 backdrop-blur-sm">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Terms & Conditions</h1>
          <p className="text-blue-100 text-lg max-w-xl mx-auto">
            Please read these terms carefully before using MultiSellerPro™. They govern your use of our platform, extension, and services.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-2 text-sm text-blue-100 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Quick Summary Banner */}
      <div className="max-w-4xl mx-auto px-6 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { emoji: '📋', title: 'Clear Terms', desc: 'No hidden clauses or confusing legalese' },
            { emoji: '💳', title: 'Easy Cancellation', desc: 'Cancel anytime through your account settings' },
            { emoji: '⚖️', title: 'Fair Use', desc: 'Designed for legitimate sellers & resellers' },
          ].map(item => (
            <div key={item.title} className="flex items-start gap-3">
              <span className="text-2xl">{item.emoji}</span>
              <div>
                <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Accordion */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">Full Terms</h2>
          <div className="flex gap-2">
            <button onClick={expandAll} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
              Expand All
            </button>
            <button onClick={collapseAll} className="text-xs text-gray-500 hover:text-gray-700 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              Collapse All
            </button>
          </div>
        </div>

        <div>
          {sections.map(section => (
            <AccordionSection
              key={section.id}
              section={section}
              isOpen={openSections.has(section.id)}
              onToggle={() => toggle(section.id)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 text-center border-t border-gray-200 pt-8">
          <p className="text-gray-500 text-sm">
            Questions about these Terms?{' '}
            <a href="mailto:contact@multisellerpro.com" className="text-blue-600 font-medium hover:underline inline-flex items-center gap-1">
              Contact us <ExternalLink className="w-3 h-3" />
            </a>
          </p>
          <p className="text-gray-400 text-xs mt-2">
            © {new Date().getFullYear()} MultiSellerPro™. All rights reserved. •{' '}
            <a href="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;